import { useEffect, useState } from "react";
import { UserPlus, Trash2, Loader2, LogOut, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageHeader, Panel } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { api, formatError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandingPanel, TransferPanel, ReplyToPanel, BrandingHistory } from "@/components/OrgSettings";
import { OrgIdPolicy } from "@/components/IdPolicy";
import { SendingDomainPanel } from "@/components/SendingDomain";

const ROLES = [["admin", "Admin"], ["sender", "Sender"], ["viewer", "Viewer"]];
const RANK = { viewer: 1, sender: 2, admin: 3, owner: 4 };
export const ROLE_INFO = {
  owner: "Billing, API keys, members and everything below",
  admin: "Manage members, plus everything below",
  sender: "Send letters and batches, see all organisation letters",
  viewer: "Read-only, with reports and certificates",
};

const RoleSelect = ({ value, onChange, max, testId }) => (
  <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm">
    {ROLES.filter(([r]) => RANK[r] <= RANK[max]).map(([r, l]) => <option key={r} value={r}>{l}</option>)}
  </select>
);

const InviteForm = ({ role, onDone }) => {
  const [f, setF] = useState({ email: "", role: "sender" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/org/invites", f);
      toast.success(`Invitation sent to ${f.email}`);
      setF({ email: "", role: "sender" });
      onDone();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap gap-3" data-testid="team-invite-form">
      <Input data-testid="team-invite-email-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="colleague@company.com" className="h-10 flex-1 min-w-[220px]" />
      <RoleSelect testId="team-invite-role-select" value={f.role} onChange={(r) => setF({ ...f, role: r })} max={role} />
      <button data-testid="team-invite-submit" disabled={busy} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Send invite
      </button>
    </form>
  );
};

const MemberRow = ({ m, me, org, onChange }) => {
  const manage = org.perms.includes("members:manage") && m.org_role !== "owner" && m.id !== me.id && RANK[m.org_role] <= RANK[org.role];
  const k = m.id.slice(0, 8);
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <tr data-testid={`team-member-${k}`} className="border-b last:border-0 border-slate-100 dark:border-slate-800">
      <td className="px-4 py-3"><p className="font-medium">{m.name}{m.id === me.id && <span className="text-xs text-slate-500"> (you)</span>}</p><p className="text-xs text-slate-500">{m.email}</p></td>
      <td className="px-4 py-3">{manage
        ? <RoleSelect testId={`team-role-select-${k}`} value={m.org_role} max={org.role} onChange={(r) => act(() => api.patch(`/org/members/${m.id}`, { role: r }), `Role changed to ${r}`)} />
        : <span data-testid={`team-role-${k}`} className="text-xs uppercase tracking-wider font-semibold">{m.org_role}</span>}</td>
      <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{fmtDate(m.org_joined_at)}</td>
      <td className="px-4 py-3 text-right">{manage && (
        <button data-testid={`team-remove-${k}`} onClick={() => window.confirm(`Remove ${m.name} from ${org.name}? They lose access immediately.`) && act(() => api.delete(`/org/members/${m.id}`), "Member removed")}
          className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-red-300 text-red-600 text-xs font-semibold hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 size={12} /> Remove</button>)}</td>
    </tr>
  );
};

const OrgName = ({ org, onChange }) => {
  const [name, setName] = useState(org.name);
  const [edit, setEdit] = useState(false);
  if (!edit) return <button data-testid="team-rename-btn" onClick={() => setEdit(true)} className="inline-flex items-center gap-1 text-xs underline"><Pencil size={12} /> Rename</button>;
  const save = async () => {
    try { await api.patch("/org", { name }); toast.success("Organisation renamed"); setEdit(false); onChange(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <span className="inline-flex gap-2"><Input data-testid="team-rename-input" value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-64" />
      <button data-testid="team-rename-save" onClick={save} className="text-xs underline">Save</button></span>
  );
};

export default function Team() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [org, setOrg] = useState(null);
  const [error, setError] = useState("");
  const load = () => api.get("/org").then((r) => setOrg(r.data)).catch((e) => setError(formatError(e)));
  useEffect(() => { load(); }, []);
  if (error) return <div data-testid="team-none" className="max-w-xl"><PageHeader eyebrow="Enterprise" title="Team" /><Panel><p className="text-sm">{error}. Ask your organisation's owner or admin to invite you.</p></Panel></div>;
  if (!org) return <Loader2 className="animate-spin text-emerald-600" />;
  const manage = org.perms.includes("members:manage");
  const leave = async () => {
    if (!window.confirm(`Leave ${org.name}? You'll lose access to its letters immediately.`)) return;
    try { await api.delete(`/org/members/${user.id}`); await refresh(); nav("/app"); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="team-page" className="max-w-5xl space-y-6">
      <PageHeader eyebrow={`Organisation · your role: ${org.role}`} title={org.name}>
        <div className="flex items-center gap-3">
          {manage && <OrgName org={org} onChange={async () => { await load(); refresh(); }} />}
          {org.role !== "owner" && <button data-testid="team-leave-btn" onClick={leave} className="inline-flex items-center gap-1 text-xs underline text-red-600"><LogOut size={12} /> Leave organisation</button>}
        </div>
      </PageHeader>
      {manage && <Panel title="Invite a colleague"><p className="text-sm text-slate-500 mb-4">They'll get an email link valid for 7 days and must sign up or sign in with that exact address.</p><InviteForm role={org.role} onDone={load} /></Panel>}
      <Panel title={`Members (${org.members.length})`}>
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="team-members-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-800">{["Member", "Role", "Joined", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
          <tbody>{org.members.map((m) => <MemberRow key={m.id} m={m} me={user} org={org} onChange={load} />)}</tbody>
        </table></div>
      </Panel>
      {manage && !!org.invites.length && (
        <Panel title="Pending invitations">
          <div className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="team-invites-list">{org.invites.map((i) => (
            <div key={i.id} data-testid={`team-invite-${i.id.slice(0, 8)}`} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
              <span>{i.email} · <span className="uppercase text-xs font-semibold">{i.role}</span> <span className="text-xs text-slate-500">· expires {fmtDate(i.expires_at)}</span></span>
              <button data-testid={`team-invite-revoke-${i.id.slice(0, 8)}`} onClick={async () => { try { await api.delete(`/org/invites/${i.id}`); toast.success("Invite revoked"); load(); } catch (e) { toast.error(formatError(e)); } }} className="text-xs underline text-red-600">Revoke</button>
            </div>))}</div>
        </Panel>
      )}
      {manage && org.enterprise && <Panel title="White-label branding" testId="team-branding"><BrandingPanel org={org} onSaved={setOrg} /></Panel>}
      {manage && org.enterprise && <Panel title="Reply-to inbox" testId="team-reply-to"><ReplyToPanel org={org} onSaved={setOrg} /></Panel>}
      {manage && org.enterprise && <Panel title="Custom sending domain" testId="team-sending-domain"><SendingDomainPanel /></Panel>}
      {manage && <Panel title="Branding history" testId="team-branding-history"><BrandingHistory key={JSON.stringify([org.brand, org.reply_to, org.name])} /></Panel>}
      {org.role === "owner" && <Panel title="Identity checks" testId="team-id-policy"><OrgIdPolicy /></Panel>}
      {org.role === "owner" && <Panel title="Transfer ownership" testId="team-transfer"><TransferPanel org={org} onDone={async () => { await refresh(); await load(); }} /></Panel>}
      <Panel title="Roles">
        <dl className="grid sm:grid-cols-2 gap-3 text-sm">{Object.entries(ROLE_INFO).map(([r, d]) => <div key={r}><dt className="font-semibold capitalize">{r}</dt><dd className="text-slate-500 text-xs mt-0.5">{d}</dd></div>)}</dl>
        <p className="text-xs text-slate-500 mt-4">Every invite, role change and removal is recorded in the audit trail.</p>
      </Panel>
    </div>
  );
}
